import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react'
import { AevocamViewer } from './AevocamViewer'
import { clampPanToKeepViewerVisible } from './layoutZoomMath'
import { useAevocamPlayer } from './useAevocamPlayer'
import './AevocamViewport.css'
import { AevocamScrubber } from './AevocamScrubber'

function touchSeparation(a: Touch, b: Touch): number {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function pinchMidInViewport(touches: TouchList, rect: DOMRect): { x: number; y: number } {
  const p = touches[0]!
  const q = touches[1]!
  return {
    x: (p.clientX + q.clientX) / 2 - rect.left,
    y: (p.clientY + q.clientY) / 2 - rect.top,
  }
}

function isFromScrubberTarget(target: EventTarget | null): boolean {
  return target instanceof Element && target.closest('.AevocamScrubber') !== null
}

type ViewportTouchCtx = {
  loadingStage: number
  actions: {
    focusPlayerShell: () => void
    applyPanOffset: (panX: number, panY: number) => void
    pinchZoomAtViewportPoint: (scaleRatio: number, vx: number, vy: number) => void
  }
  clampPanLocal: (nextPanX: number, nextPanY: number) => { panX: number; panY: number }
  zoomFit: boolean
  viewerFitsInViewport: () => boolean
  skipNextViewportClickRef: RefObject<boolean>
}

export type AevocamViewportProps = {
  withScrubber?: boolean
  children?: ReactNode
}

/**
 * Clipped region that measures as the player viewport, centers the viewer, and applies pan.
 * Touch: one-finger pan (when not letterboxed fit), two-finger pinch zoom at the pinch midpoint (`touch-action: none`).
 * Not keyboard-focusable; shortcuts target {@link AevocamPlayerShell} (Tab or click on chrome).
 * If focus is outside {@link AevocamPlayerShell} at viewport mousedown (before focus moves), that click only lands focus on the shell; otherwise play/pause toggles.
 * Use under {@link AevocamPlayerProvider} instead of mounting {@link AevocamViewer} alone
 * when the viewer should fit the available area (see {@link AevocamPlayer}).
 */
export function AevocamViewport({
  withScrubber,
  children,
}: AevocamViewportProps) {

  const {
    viewportRef,
    skipNextViewportClickRef,
    deferPlayPauseRef,
    panX,
    panY,
    loadingStage,
    isPlaying,
    playReverse,
    zoomFit,
    viewportSize,
    viewerViewWidth,
    viewerViewHeight,
    actions,
    viewerFitsInViewport,
  } = useAevocamPlayer()

  const [isDragging, setIsDragging] = useState(false)
  const dragStartRef = useRef<{ x: number; y: number; panX: number; panY: number } | null>(null)

  const touchModeRef = useRef<'none' | 'pan' | 'pinch'>('none')
  const panTouchStartRef = useRef<{
    id: number
    x: number
    y: number
    panX: number
    panY: number
  } | null>(null)
  const pinchLastSepRef = useRef(0)
  const panLiveRef = useRef({ panX: 0, panY: 0 })
  panLiveRef.current = { panX, panY }

  const touchCtxRef = useRef<ViewportTouchCtx | null>(null)

  //--------------------------------------------------------------------------------
  const clampPanLocal = useCallback(
    (nextPanX: number, nextPanY: number) =>
      clampPanToKeepViewerVisible(nextPanX, nextPanY, viewportSize, {
        width: viewerViewWidth,
        height: viewerViewHeight,
      }),
    [viewportSize, viewerViewWidth, viewerViewHeight],
  )

  //--------------------------------------------------------------------------------
  touchCtxRef.current = {
    loadingStage,
    actions,
    clampPanLocal,
    zoomFit,
    viewerFitsInViewport,
    skipNextViewportClickRef,
  }

  //--------------------------------------------------------------------------------
  const handleWheelNative = useCallback((ev: WheelEvent) => {
    if (isFromScrubberTarget(ev.target)) return
    if (loadingStage < 2) return
    if (ev.deltaY === 0) return

    if (ev.shiftKey) {
      const vp = viewportRef.current
      if (!vp) return
      const rect = vp.getBoundingClientRect()
      const x = ev.clientX - rect.left
      const y = ev.clientY - rect.top

      // Guardrail: only zoom when the cursor is over the viewer bounds.
      const viewerLeft = viewportSize.width / 2 - viewerViewWidth / 2 + panX
      const viewerTop = viewportSize.height / 2 - viewerViewHeight / 2 + panY
      if (
        x < viewerLeft ||
        x > viewerLeft + viewerViewWidth ||
        y < viewerTop ||
        y > viewerTop + viewerViewHeight
      ) {
        return
      }

      ev.preventDefault()
      ev.stopPropagation()
      actions.zoomStepTowardViewportPoint(-ev.deltaY, x, y)
      return
    }

    ev.preventDefault()
    ev.stopPropagation()
    actions.stop()
    actions.advance(Math.sign(ev.deltaY))
  }, [
    loadingStage,
    actions,
    viewportRef,
    viewportSize,
    viewerViewWidth,
    viewerViewHeight,
    panX,
    panY,
  ])

  //--------------------------------------------------------------------------------
  const handleClick = useCallback((ev: React.MouseEvent<HTMLDivElement>) => {
    if (isFromScrubberTarget(ev.target)) return
    if (loadingStage < 2) return
    if (skipNextViewportClickRef.current) {
      skipNextViewportClickRef.current = false
      deferPlayPauseRef.current = false
      return
    }
    if (deferPlayPauseRef.current) {
      deferPlayPauseRef.current = false
      return
    }
    if (isPlaying) {
      actions.stop()
    } else {
      if (ev.shiftKey) {
        actions.setDirection(!playReverse)
      }
      actions.play(true)
    }
  }, [loadingStage, actions, isPlaying, playReverse, deferPlayPauseRef])

  //--------------------------------------------------------------------------------
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (isFromScrubberTarget(e.target)) return
    e.preventDefault()
    e.stopPropagation()
    if (loadingStage < 2) return
    if (e.button !== 0) return
    actions.focusPlayerShell()
    // In fit mode, pan only when the laid-out viewer actually overflows the viewport.
    // When zoomFit is off (manual zoom / shift-wheel), allow pan even if the image is
    // letterboxed so it matches relaxed centering and zoom-to-pointer behavior.
    if (zoomFit && viewerFitsInViewport()) return

    setIsDragging(true)
    skipNextViewportClickRef.current = false
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      panX,
      panY,
    }
    e.preventDefault()
    e.stopPropagation()
  }, [loadingStage, actions, zoomFit, viewerFitsInViewport, panX, panY])

  //--------------------------------------------------------------------------------
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging || !dragStartRef.current) return

    const deltaX = e.clientX - dragStartRef.current.x
    const deltaY = e.clientY - dragStartRef.current.y

    if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
      skipNextViewportClickRef.current = true
    }

    const { panX: nextPanX, panY: nextPanY } = clampPanLocal(
      dragStartRef.current.panX + deltaX,
      dragStartRef.current.panY + deltaY,
    )
    actions.applyPanOffset(nextPanX, nextPanY)
  }, [isDragging, actions, clampPanLocal])

  //--------------------------------------------------------------------------------
  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
    dragStartRef.current = null
  }, [])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    if (!isDragging) return
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    const vp = viewportRef.current
    if (!vp) return

    vp.addEventListener('wheel', handleWheelNative, { passive: false })
    return () => {
      vp.removeEventListener('wheel', handleWheelNative)
    }
  }, [viewportRef, handleWheelNative])

  //--------------------------------------------------------------------------------
  useEffect(() => {
    const vp = viewportRef.current
    if (!vp) return

    const onTouchStart = (e: TouchEvent) => {
      if (isFromScrubberTarget(e.target)) return
      const c = touchCtxRef.current
      if (!c || c.loadingStage < 2) return
      c.actions.focusPlayerShell()

      if (e.touches.length >= 2) {
        e.preventDefault()
        touchModeRef.current = 'pinch'
        panTouchStartRef.current = null
        const a = e.touches[0]!
        const b = e.touches[1]!
        const d = touchSeparation(a, b)
        pinchLastSepRef.current = d > 1e-6 ? d : 1
        return
      }

      if (e.touches.length === 1) {
        if (c.zoomFit && c.viewerFitsInViewport()) return
        const t = e.touches[0]
        if (!t) return
        touchModeRef.current = 'pan'
        const live = panLiveRef.current
        panTouchStartRef.current = {
          id: t.identifier,
          x: t.clientX,
          y: t.clientY,
          panX: live.panX,
          panY: live.panY,
        }
        c.skipNextViewportClickRef.current = false
      }
    }

    const onTouchMove = (e: TouchEvent) => {
      if (isFromScrubberTarget(e.target)) return
      const c = touchCtxRef.current
      if (!c || c.loadingStage < 2) return

      if (e.touches.length >= 2) {
        e.preventDefault()
        touchModeRef.current = 'pinch'
        panTouchStartRef.current = null
        const a = e.touches[0]!
        const b = e.touches[1]!
        const d = touchSeparation(a, b)
        const prev = pinchLastSepRef.current
        if (prev <= 1e-6) {
          pinchLastSepRef.current = d > 1e-6 ? d : 1
          return
        }
        const ratio = d / prev
        pinchLastSepRef.current = d
        const rect = vp.getBoundingClientRect()
        const mid = pinchMidInViewport(e.touches, rect)
        c.actions.pinchZoomAtViewportPoint(ratio, mid.x, mid.y)
        c.skipNextViewportClickRef.current = true
        return
      }

      if (
        touchModeRef.current === 'pan'
        && panTouchStartRef.current
        && e.touches.length === 1
      ) {
        const t = Array.from(e.touches).find(
          u => u.identifier === panTouchStartRef.current!.id,
        )
        if (!t) return
        e.preventDefault()
        const start = panTouchStartRef.current
        const deltaX = t.clientX - start.x
        const deltaY = t.clientY - start.y
        if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
          c.skipNextViewportClickRef.current = true
        }
        const { panX: nx, panY: ny } = c.clampPanLocal(
          start.panX + deltaX,
          start.panY + deltaY,
        )
        c.actions.applyPanOffset(nx, ny)
      }
    }

    const onTouchEndOrCancel = (e: TouchEvent) => {
      if (isFromScrubberTarget(e.target)) return
      if (e.touches.length === 0) {
        touchModeRef.current = 'none'
        panTouchStartRef.current = null
        pinchLastSepRef.current = 0
        return
      }
      if (e.touches.length === 1 && touchModeRef.current === 'pinch') {
        touchModeRef.current = 'pan'
        const t = e.touches[0]
        if (!t) return
        const live = panLiveRef.current
        panTouchStartRef.current = {
          id: t.identifier,
          x: t.clientX,
          y: t.clientY,
          panX: live.panX,
          panY: live.panY,
        }
        pinchLastSepRef.current = 0
      }
    }

    const opts: AddEventListenerOptions = { passive: false }
    vp.addEventListener('touchstart', onTouchStart, opts)
    vp.addEventListener('touchmove', onTouchMove, opts)
    vp.addEventListener('touchend', onTouchEndOrCancel)
    vp.addEventListener('touchcancel', onTouchEndOrCancel)
    return () => {
      vp.removeEventListener('touchstart', onTouchStart, opts)
      vp.removeEventListener('touchmove', onTouchMove, opts)
      vp.removeEventListener('touchend', onTouchEndOrCancel)
      vp.removeEventListener('touchcancel', onTouchEndOrCancel)
    }
  }, [viewportRef])

  //--------------------------------------------------------------------------------
  return (
    <div
      ref={viewportRef}
      className='AevocamViewport'
      onMouseDown={handleMouseDown}
      onClick={handleClick}
    >
      <div
        className='Stage'
        style={{
          transform: `translate(calc(-50% + ${panX}px), calc(-50% + ${panY}px))`,
          cursor: isDragging ? 'grabbing' : 'grab',
        }}
      >
        {children ?? <AevocamViewer />}
      </div>
      {withScrubber && <AevocamScrubber tabIndex={-1} />}
    </div>
  )
}
